/**
 * Chat storage — the organisation's recent cleanups (B2 step ③, todo #6).
 *
 * ── Why this exists ───────────────────────────────────────────────────
 *
 * A booked cleanup ran in the background and left nothing on any screen:
 * one that finished looked exactly like one that gave up after fifty tries
 * with thousands deleted (`listFailed` had no caller). And D35 (step ④)
 * takes away the one place a result was ever shown — the run-now dialog's
 * "deleted 200" — so the admin needs somewhere to look back.
 *
 * ── "At least", every time ────────────────────────────────────────────
 *
 * The counts are LOWER bounds (D34): a worker killed mid-chunk loses that
 * chunk's tally. Saying "200 deleted" about a booking that may have taken
 * 220 would be the confident wrong number this page avoids everywhere else.
 *
 * Opened from a button beside Clean up (Season 2026-10-22: a button, not a
 * standing panel); the list is the parent's, so a cancel here refreshes it
 * with the rest of the page.
 */
import { useState } from 'react'
import { activityUnderWay } from './cleanupVerdict'
import { cleanupRowFigures } from './cleanupRowFigures'
import { useTranslation } from '@papan/i18n'
import { nameFromEmail } from '@papan/core-ui/ui'
import type { CleanupActivityRow } from '../../client/storage'
import { formatBytes } from '../budget/bits'

export function CleanupActivityDialog({ rows, canManage, cancelRaceLost = false, cancelOutcome = null, onCancel, onClose }: {
  rows: CleanupActivityRow[]
  /** Cancel is a write; a read-only viewer sees the list without it. */
  canManage: boolean
  /** A cancel lost the race to the worker. Said HERE too: the page's own
   *  line sits behind this dialog. */
  cancelRaceLost?: boolean
  /** What the last cancel or stop did — said here too, for the same reason. */
  cancelOutcome?: CancelOutcome | null
  /** Resolves once the cancel has answered and the list is reloaded. */
  onCancel: (id: string) => Promise<void>
  onClose: () => void
}) {
  const { t } = useTranslation('core-ui')
  // ONE cancel at a time: a second click while the first is out sent a
  // second request for the same booking, which answered 409 and put up
  // the lost-race line about a cancel that had in fact worked.
  const [cancelling, setCancelling] = useState<string | null>(null)
  const cancel = async (id: string) => {
    setCancelling(id)
    try { await onCancel(id) } finally { setCancelling(null) }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50 ws-anim-fade-in" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="cleanup-activity-title"
        className="relative z-10 max-w-xl w-full max-h-[85vh] flex flex-col ws-glass-modal ws-anim-modal-in"
      >
        <div className="px-6 pt-5 pb-3.5 themed-border border-b">
          <h3 id="cleanup-activity-title" className="text-[17px] font-semibold themed-text">
            {t('settings.storage.cleanupActivityTitle')}
          </h3>
          <p className="text-xs themed-text-muted mt-1">{t('settings.storage.cleanupActivitySubtitle')}</p>
        </div>

        {cancelRaceLost && (
          <p className="px-6 pt-3 text-xs text-amber-500 leading-relaxed">{t('settings.storage.cleanupCancelRaceLost')}</p>
        )}
        {cancelOutcome && <CancelOutcomeLine outcome={cancelOutcome} className="px-6 pt-3" />}
        <div className="overflow-y-auto min-h-0 flex-1">
          {rows.map((r) => <ActivityRow key={r.id} r={r} canManage={canManage} busy={cancelling !== null} onCancel={(id) => { void cancel(id) }} />)}
        </div>

        <div className="px-6 py-3.5 themed-border border-t flex justify-end">
          <button type="button" onClick={onClose} className="text-[13px] px-4 py-2 rounded-lg themed-border border themed-text">
            {t('common:close')}
          </button>
        </div>
      </div>
    </div>
  )
}

/** The dot's colour by state: indigo booked or running (the member row's
 *  badge colour), emerald finished, rose given up, muted cancelled. */
const DOT: Record<CleanupActivityRow['state'], string> = {
  scheduled: 'bg-indigo-400',
  running: 'bg-indigo-400',
  done: 'bg-emerald-600',
  failed: 'bg-rose-500',
  cancelled: 'border-[1.5px] border-current themed-text-secondary',
}

function ActivityRow({ r, canManage, busy, onCancel }: {
  r: CleanupActivityRow
  canManage: boolean
  /** A cancel is in flight — every Cancel waits for it. */
  busy: boolean
  onCancel: (id: string) => void
}) {
  const { t } = useTranslation('core-ui')
  const who = r.target.kind === 'member'
    ? nameFromEmail(r.target.email)
    : t('settings.storage.chatStorageUnattributed')
  const k = r.deletedCount
  /** UNDER WAY — the server's one rule (`cleanupUnderWay`, Season
   *  2026-10-22 option A): running now, between passes (`resumed`), or due
   *  and not yet taken by a worker (`due`). */
  const inProgress = activityUnderWay(r)
  // WHICH FIGURES THIS ROW STATES — `cleanupRowFigures`, not inline, and
  // for the reason the bell's own rule moved out of its emit site: it is a
  // CHOICE between figures that look alike, and the only guard that holds
  // such a choice is one that exercises it with values. Nine source-reading
  // versions of the bell's guard each failed to hold the rule they named.
  // The component draws what it is handed and decides nothing.
  // It hands back the COPY KEY for each figure, so the hedge ("at least")
  // cannot be paired with the wrong number here — that pairing, separable
  // at the call site, is the defect that took nine versions to close on the
  // bell side.
  // SETTLED = the booking has stopped moving, so its totals are final. A
  // `stopping` row is still winding its current batch down, so it is not
  // settled either — its figures are as partial as a running one's.
  const settled = !inProgress && r.stop !== 'stopping'
  const fig = cleanupRowFigures(r, formatBytes, settled)
  const stated = [fig.released, fig.releasedMembers, fig.orgReleased, fig.ceiling]
    .filter((f): f is NonNullable<typeof f> => f !== undefined)

  const when = (() => {
    switch (r.state) {
      case 'scheduled': return r.resumed
        ? t('settings.storage.cleanupWhenResumes', { when: formatWhen(r.runAt) })
        // Due, not taken yet: it began when its time came.
        : inProgress ? t('settings.storage.cleanupWhenStarted', { when: formatWhen(r.runAt) })
        : t('settings.storage.cleanupWhenRuns', { when: formatWhen(r.runAt) })
      case 'running': return t('settings.storage.cleanupWhenStarted', { when: formatWhen(r.startedAt ?? r.runAt) })
      case 'done': return t('settings.storage.cleanupWhenFinished', { when: formatWhen(r.finishedAt ?? r.runAt) })
      case 'cancelled':
        // A stop still winding down has no `finishedAt` yet; it started.
        if (r.stop === 'stopping') return t('settings.storage.cleanupWhenStarted', { when: formatWhen(r.startedAt ?? r.runAt) })
        if (r.stop === 'stopped') return t('settings.storage.cleanupWhenStopped', { when: formatWhen(r.finishedAt ?? r.runAt) })
        return t('settings.storage.cleanupWhenCancelled', { when: formatWhen(r.finishedAt ?? r.runAt) })
      case 'failed': return t('settings.storage.cleanupWhenGaveUp', { when: formatWhen(r.finishedAt ?? r.runAt) })
    }
  })()

  // What it did — always "at least" (D34). ⚠️ NEVER "NOTHING WAS
  // DELETED": a pass killed part-way loses its chunk's tally, so a 0 or a
  // null only means none was RECORDED. "No deletions were recorded" is the
  // most the row can say.
  const n = r.conversations
  const result = (() => {
    switch (r.state) {
      case 'scheduled':
        if (inProgress) {
          // Between passes: what it has done so far, as RUNNING says it.
          if (k) return n === null
            ? t('settings.storage.cleanupResultRunningSoFar', { count: k })
            : t('settings.storage.cleanupResultRunning', { count: k, total: n })
          return n === null ? t('settings.storage.cleanupResultRunningNoneYet') : t('settings.storage.cleanupResultRunningNone', { count: n })
        }
        // No list (`null`) — a booking that re-plans from its criteria:
        // the criteria are all there is to say.
        return n === null
          ? t('settings.storage.cleanupResultScheduledNoList', { days: r.idleDays })
          : t('settings.storage.cleanupResultScheduled', { count: n, days: r.idleDays })
      case 'running':
        if (k) {
          return n === null
            ? t('settings.storage.cleanupResultRunningSoFar', { count: k })
            : t('settings.storage.cleanupResultRunning', { count: k, total: n })
        }
        return n === null
          ? t('settings.storage.cleanupResultRunningNoneYet')
          : t('settings.storage.cleanupResultRunningNone', { count: n })
      case 'done':
        return k ? t('settings.storage.cleanupResultDone', { count: k }) : t('settings.storage.cleanupResultNoneRecorded')
      case 'cancelled':
        // ⚠️ A booking can be cancelled BETWEEN passes, after some of its
        // list is already gone — the case todo's issue 3 is about. Those
        // are not coming back, and the row must not read as "nothing
        // happened" — nor, with no count, as "cancelled before it ran":
        // a requeued booking is `scheduled` again and cancellable.
        // STOPPING: the worker is finishing the batch in hand (Season
        // 2026-10-22: no half batch, and no second Stop button).
        if (r.stop === 'stopping') return t('settings.storage.cleanupResultStopping')
        if (r.stop === 'stopped') {
          return k ? t('settings.storage.cleanupResultStoppedAfter', { count: k }) : t('settings.storage.cleanupResultStoppedNone')
        }
        return k ? t('settings.storage.cleanupResultCancelledAfter', { count: k }) : t('settings.storage.cleanupResultCancelledNone')
      case 'failed':
        return k ? t('settings.storage.cleanupResultGaveUp', { count: k }) : t('settings.storage.cleanupResultNoneRecorded')
    }
  })()

  // HOW MANY WERE LEFT, where the row can know it. The frozen list's size
  // minus what was recorded as deleted — and `null` wherever that answer
  // would be a guess: a booking with no list (it re-plans from its
  // criteria), or one whose deleted count was never written.
  //
  // ⚠️ AND IT MUST BE ALLOWED TO BE ZERO-OR-UNKNOWN. The copy that names a
  // number asserts that conversations were LEFT, and for a cleanup the
  // patrol closed we may not know that: the worker can have finished its
  // last batch and died before writing "done". Where the number is not
  // known, or is zero, the sentence drops the clause instead of inventing
  // one. Season 2026-10-28 asked for the number; this is what makes the
  // number honest.
  const left = typeof r.conversations === 'number' && typeof r.deletedCount === 'number'
    && r.conversations > r.deletedCount
    ? r.conversations - r.deletedCount
    : null

  // WHY IT STOPPED — and the two live cases stay APART on purpose.
  //
  // Both now open with "the cleanup processed these conversations", because
  // the old `abandoned` text said "It stopped without recording a result"
  // directly above a line reading "At least 400 conversations deleted ·
  // their usage dropped by 500 MB" (Season found this 2026-10-28). That is
  // self-contradictory, and it is not a rare state: the patrol closes any
  // booking whose worker went away, and the commonest reason for that is a
  // deploy restarting the worker mid-run.
  //
  // What they must NOT lose is which of the two it was, because that is the
  // only thing here an admin can act on:
  //   retries   — it tried to the cap and some conversations were IN USE.
  //               Re-booking will meet the same ones until those people
  //               close them.
  //   abandoned — its worker went away; nothing is wrong with the
  //               conversations. Re-booking will most likely just work.
  const why = r.state !== 'failed' ? null
    : r.failureKind === 'retries'
      ? (left !== null ? t('settings.storage.cleanupWhyRetriesLeft', { count: left }) : t('settings.storage.cleanupWhyRetries'))
    : r.failureKind === 'abandoned'
      ? (left !== null ? t('settings.storage.cleanupWhyStoppedLeft', { count: left }) : t('settings.storage.cleanupWhyStopped'))
    // A run-now that DID its work and then could not write it down — its
    // count comes from its own failure note. Not "an error": what it was
    // asked to do happened.
    : r.failureKind === 'unrecorded' ? t('settings.storage.cleanupWhyUnrecorded')
    : t('settings.storage.cleanupWhyError')

  const label: Record<CleanupActivityRow['state'], string> = {
    scheduled: t('settings.storage.cleanupStateScheduled'),
    running: t('settings.storage.cleanupStateRunning'),
    done: t('settings.storage.cleanupStateDone'),
    cancelled: t('settings.storage.cleanupStateCancelled'),
    // INCOMPLETE, not GAVE UP (Season 2026-10-28). Every `failed` row has
    // done SOME of its list — the chip sits above a line counting what it
    // deleted — and "gave up" reads as "did nothing". What is true of all
    // four failure kinds is that the list was not finished.
    failed: t('settings.storage.cleanupStateIncomplete'),
  }
  const stateLabel = r.state === 'scheduled' && inProgress ? t('settings.storage.cleanupStateRunning')
    : r.stop === 'stopping' ? t('settings.storage.cleanupStateStopping')
    : r.stop === 'stopped' ? t('settings.storage.cleanupStateStopped')
    : label[r.state]

  return (
    <div className="px-6 py-3 themed-border border-b last:border-b-0 flex items-start gap-3">
      <span className={`mt-1.5 shrink-0 w-2.5 h-2.5 rounded-full ${r.stop === 'stopping' || inProgress ? DOT.running : r.stop === 'stopped' ? DOT.failed : DOT[r.state]}`} aria-hidden="true" />
      <div className="min-w-0 flex-1 space-y-0.5">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="text-[13px] font-semibold themed-text">{who}</span>
          <span className="text-[10px] font-bold tracking-wider themed-text-secondary">{stateLabel}</span>
          <span className="text-xs themed-text-muted">{when}</span>
        </div>
        <p className="text-xs themed-text leading-relaxed">
          {result}
          {(r.state !== 'scheduled' || inProgress) && stated.map((f) => (
            <span key={f.key}> · {t(f.key, { size: f.size })}</span>
          ))}
        </p>
        {fig.sharedNote && (r.state !== 'scheduled' || inProgress) && (
          <p className="text-xs themed-text-muted leading-relaxed">{t('settings.storage.cleanupResultSharedNote')}</p>
        )}
        {why && <p className="text-xs text-rose-500 leading-relaxed">{why}</p>}
      </div>
      {canManage && inProgress && (
        // STOP (D35 step ④). The same call as Cancel; on a running booking
        // the server stops it after the batch in hand and tells the member.
        <button
          type="button"
          disabled={busy}
          onClick={() => onCancel(r.id)}
          className="shrink-0 text-xs px-2.5 py-1 rounded-lg border border-rose-400/50 text-rose-400 font-semibold disabled:opacity-50"
        >
          {t('settings.storage.cleanupStop')}
        </button>
      )}
      {canManage && r.state === 'scheduled' && !inProgress && (
        <button
          type="button"
          disabled={busy}
          onClick={() => onCancel(r.id)}
          className="shrink-0 text-xs px-2.5 py-1 rounded-lg themed-border border themed-text disabled:opacity-50"
        >
          {t('settings.storage.cleanupCancelScheduled')}
        </button>
      )}
    </div>
  )
}

/** The date and time it happened, in the viewer's own terms. */
function formatWhen(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
}

/** What a cancel or stop did (issues-to-file #3, ⑤). */
export interface CancelOutcome {
  /** The booking it was about — how a STOPPING line finds out it stopped. */
  id?: string
  result: 'cancelled' | 'stopped' | 'stopping'
  /** Deleted by then — a LOWER bound, as everywhere (D34). */
  deleted: number | null
}

/**
 * The sentence after a cancel or a stop. "At least N were already deleted
 * and cannot be brought back" is the one fact the admin did not have: the
 * badge simply went, and they reasonably took it that nothing had
 * happened, while the member was N conversations short.
 */
export function CancelOutcomeLine({ outcome, className = '' }: { outcome: CancelOutcome; className?: string }) {
  const { t } = useTranslation('core-ui')
  const n = outcome.deleted ?? 0
  const text = outcome.result === 'cancelled'
    ? t('settings.storage.cleanupOutcomeCancelled')
    : outcome.result === 'stopping'
      ? (n > 0 ? t('settings.storage.cleanupOutcomeStoppingAfter', { count: n }) : t('settings.storage.cleanupOutcomeStopping'))
      : (n > 0 ? t('settings.storage.cleanupOutcomeStoppedAfter', { count: n }) : t('settings.storage.cleanupOutcomeStoppedNone'))
  return (
    <p data-testid="cancel-outcome" className={`text-[12px] leading-relaxed ${n > 0 ? 'text-amber-500' : 'themed-text-muted'} ${className}`}>
      {text}
    </p>
  )
}

